import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useLiveQuery } from 'dexie-react-hooks'
import { useTranslation } from 'react-i18next'
import { attachmentStorageKey, newId, type AssetCostReport, type CostFile, type CostLine } from '@gk/contracts'
import { useClub } from '../club/ClubProvider.tsx'
import { useStockMovements } from '../data/catalog.ts'
import type { AssetRow } from '../data/fleet.ts'
import {
  ageOf, blankCostForm, buildCostPayload, buildFinancialPayload, COST_KINDS, COST_NOTE_MAX, costKindText, costProblem, costProblemText, costRows, dayText, fileSizeText,
  fileTypeText, fillUpOptions, financialFormOf, financialProblemText, fmtMoney, fmtQty, invoiceFileName, meterInPeriod, monthRows, monthText, orderOptions, PERIODS,
  periodRange, periodText, voidProblem, waitingInvoiceCounts, type CostForm, type FinancialForm, type Period,
} from '../data/finance.ts'
import { useWorkOrders } from '../data/safety.ts'
import { localDate } from '../lib/time.ts'
import { DocumentRefusedError, addDocument, PhotoQuotaError } from '../photos/store.ts'
import { DocumentTooLargeError, DocumentTypeError, prepareDocument, type PreparedDocument } from '../photos/capture.ts'
import { Banner, DisabledHint, Field, Pill } from '../ui/components.tsx'
import { StepUpField, useSensitive, type Sensitive } from '../ui/sensitive.tsx'
import { usePersonNames } from './measures/shared.tsx'
import { NoFactorBanner } from './people/Factor.tsx'
import { Fold } from './workshop/shared.tsx'

type Machine = Pick<AssetRow, 'id' | 'readable_tag' | 'make' | 'model' | 'year' | 'acquired_on' | 'lifecycle'>

/** M15-E1 (docs/M15.md E1; benchmark 57, GK-034): a machine's costs, the direction's alone (finance.read, finance.manage), on the machine's page (the "Coûts"
 *  fold) and in Réglages › Coûts. What a Superintendent brings to his board — what the machine cost in invoices over a period, the litres it took at the pump
 *  and the hours it worked BESIDE them, never priced, its meter, its age — and the sentence that repairing or replacing is a judgement. Read ONLINE after a
 *  fresh second-factor code through the audited `api.report_asset_costs` (one audit event per read, never an amount) and kept in this component's state only:
 *  nothing of it is mirrored, cached, or left in the outbox once answered (sync/transient.ts); an invoice's file is fetched with `no-store` and handed to the
 *  phone's own viewer. The financials, a cost and its void are Y* commands (useSensitive().command: nonce, the server's verdict); the report is read again
 *  after each (M5). An invoice's PDF or photo is an ordinary `attachment.register@2` on the cost (M15-V's documents bucket), sent with the next sync. */
export function AssetCosts({ asset }: { asset: Machine }) {
  const { t } = useTranslation()
  const { org, site, deviceId, can } = useClub()
  const s = useSensitive()
  const today = localDate(new Date(), site.timezone)
  const [period, setPeriod] = useState<Period>('year')
  const [asked, setAsked] = useState(false)
  const [report, setReport] = useState<AssetCostReport | null>(null)
  const range = periodRange(period, today, asset)
  const load = async (p: Period = period): Promise<boolean> => {
    setAsked(true)
    const { from, to } = periodRange(p, today, asset)
    const r = await s.call<AssetCostReport>('report_asset_costs', { p_org: org.org_id, p_asset: asset.id, p_from: from, p_to: to, p_device_id: deviceId })
    if (r) { setReport(r); setPeriod(p); return true }
    return false
  }
  // M5: read again after a change; the read clears the command's message, so it is said again once it is done
  const after = (doneKey: string) => async (ok: boolean | undefined) => { if (ok && (await load())) s.setOk(t(doneKey)) }
  // another machine (Réglages › Coûts' picker): nothing of the previous one stays on the page
  useEffect(() => { setReport(null); setAsked(false); s.reset() }, [asset.id])           // eslint-disable-line react-hooks/exhaustive-deps
  const manage = can('finance.manage')
  return (
    <div className="flex flex-col gap-4" data-testid="asset-costs" data-asset={asset.id}>
      <p className="meta">{t('costs.help')}</p>
      <NoFactorBanner testId="costs-no-factor" />
      <fieldset className="flex flex-col gap-2" data-testid="costs-period" data-from={range.from} data-to={range.to}>
        <legend className="text-sm font-semibold">{t('costs.period.title')}</legend>
        <div className="grid grid-cols-2 gap-2">
          {PERIODS.map((p) => (
            <button key={p} type="button" className={`btn !px-3 text-sm ${p === period ? '!border-brand !bg-fairway-light dark:!border-emerald-500 dark:!bg-night' : ''}`} aria-pressed={p === period}
              disabled={s.busy} onClick={() => { setPeriod(p); if (report) void load(p) }} data-testid={`costs-period-${p}`}>{periodText(t, p, today)}</button>
          ))}
        </div>
        <p className="meta" data-testid="costs-period-days">{t('costs.period.days', { from: dayText(range.from), to: dayText(range.to) })}</p>
      </fieldset>
      <StepUpField s={s} />
      {!report ? (
        <button type="button" className="btn btn-primary" disabled={s.busy || !s.online} onClick={() => void load()} data-testid="costs-show">{t('costs.show')}</button>
      ) : (
        <>
          <Banner tone="info" testId="costs-judgement">{t('costs.judgement')}</Banner>
          <Summary r={report} asset={asset} today={today} />
          <ByMonth r={report} />
          <CostList r={report} s={s} manage={manage} onDone={after} />
          {manage && <CostFormFold asset={asset} today={today} s={s} onDone={after('costs.form.saved')} />}
          {manage && <FinancialFold asset={asset} r={report} s={s} onDone={after('costs.financial.saved')} />}
          <button type="button" className="btn self-start" onClick={() => { setReport(null); setAsked(false); s.reset() }} data-testid="costs-hide">{t('costs.hide')}</button>
        </>
      )}
    </div>
  )
}

/** The machine page's fold "Coûts" (AssetDetail.tsx): nothing for whoever does not read costs. `#costs` opens it. */
export function AssetCostsSection({ asset, open }: { asset: Machine; open?: boolean }) {
  const { t } = useTranslation()
  const { can, kiosk } = useClub()
  if (kiosk || !can('finance.read')) return null
  return (
    <div id="costs" className="scroll-mt-20">
      <Fold title={t('costs.machineTitle')} open={open} testId="asset-costs-fold"><AssetCosts asset={asset} /></Fold>
    </div>
  )
}

// ---------------------------------------------------------------------------------------------------- what was recorded, in sums
function Summary({ r, asset, today }: { r: AssetCostReport; asset: Machine; today: string }) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const age = ageOf({ acquired_on: r.asset.acquired_on ?? asset.acquired_on, year: r.asset.year ?? asset.year }, today)
  const f = r.financial
  const facts: Array<[string, string, string]> = [
    ['costs.sum.total', t('costs.sum.total'), r.total.count > 0 ? fmtMoney(r.total.amount, lang) : t('costs.sum.none')],
    ['costs.sum.count', t('costs.sum.countLabel'), t('costs.sum.count', { count: r.total.count }) + (r.total.voided > 0 ? ` · ${t('costs.sum.voided', { count: r.total.voided })}` : '')],
    ['costs.sum.litres', t('costs.sum.litres'), r.litres.fills === 0 ? t('costs.sum.noFill') : `${r.litres.total !== null ? `${fmtQty(Number(r.litres.total), lang)} L` : '—'} · ${t('costs.sum.fills', { count: r.litres.fills })}${r.litres.not_in_litres > 0 ? ` · ${t('costs.sum.notInLitres', { count: r.litres.not_in_litres })}` : ''}`],
    ['costs.sum.hours', t('costs.sum.hours'), `${fmtQty(Number(r.hours.total), lang)} h${r.hours.running > 0 ? ` · ${t('costs.sum.running')}` : ''}${r.hours.flagged > 0 ? ` · ${t('costs.sum.flagged', { count: r.hours.flagged, hours: fmtQty(Number(r.hours.flagged_hours), lang) })}` : ''}`],
    ...r.meters.map((m): [string, string, string] => [`meter-${m.slot_id}`, t('costs.sum.meter', { unit: m.unit_code }),
      t('costs.sum.meterValue', { inPeriod: fmtQty(meterInPeriod(m), lang), lifetime: fmtQty(Number(m.lifetime), lang), unit: m.unit_code })]),
    ['costs.sum.age', t('costs.sum.age'), age ? (age.approx ? t('costs.sum.ageApprox', { count: age.years, year: r.asset.year ?? asset.year }) : `${t('costs.sum.years', { count: age.years })}${age.months ? ` ${t('costs.sum.months', { count: age.months })}` : ''} · ${t('costs.sum.acquiredOn', { day: dayText((r.asset.acquired_on ?? asset.acquired_on)!) })}`) : t('costs.sum.ageUnknown')],
    ['costs.sum.purchase', t('costs.sum.purchase'), f?.purchase_cost != null ? fmtMoney(Number(f.purchase_cost), lang) : t('costs.sum.notRecorded')],
    ['costs.sum.life', t('costs.sum.life'), f?.expected_life_years != null ? t('costs.sum.lifeYears', { count: f.expected_life_years }) : t('costs.sum.notRecorded')],
    ['costs.sum.residual', t('costs.sum.residual'), f?.residual_value != null ? fmtMoney(Number(f.residual_value), lang) : t('costs.sum.notRecorded')],
  ]
  return (
    <div className="card flex flex-col gap-3" data-testid="costs-summary">
      <h3 className="text-lg font-bold">{t('costs.sum.title', { from: dayText(r.from), to: dayText(r.to) })}</h3>
      <dl className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-[auto_1fr]">
        {facts.map(([id, k, v]) => (
          <div key={id} className="contents" data-testid={`costs-fact-${id.replace(/^costs\.sum\./, '')}`}>
            <dt className="font-semibold">{k}</dt><dd className="tabular-nums" data-testid={`costs-value-${id.replace(/^costs\.sum\./, '')}`}>{v}</dd>
          </div>
        ))}
      </dl>
      <p className="meta">{t('costs.sum.rule')}</p>
      {r.by_kind.length > 0 && (
        <ul className="flex flex-col gap-1" data-testid="costs-by-kind">
          {r.by_kind.map((k) => (
            <li key={k.kind} className="flex items-center justify-between gap-3" data-testid="costs-kind-row" data-kind={k.kind}>
              <span>{costKindText(t, k.kind)} <span className="meta whitespace-nowrap">· {t('costs.sum.count', { count: k.count })}</span></span>
              <span className="font-semibold tabular-nums">{fmtMoney(Number(k.amount), lang)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function ByMonth({ r }: { r: AssetCostReport }) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const rows = useMemo(() => monthRows(r), [r])
  if (rows.length === 0) return null
  return (
    <div className="card flex flex-col gap-2" data-testid="costs-months">
      <h3 className="text-lg font-bold">{t('costs.months.title')}</h3>
      <ul className="flex flex-col divide-y divide-slate-200 dark:divide-night-line">
        {rows.map((m) => (
          <li key={m.month} className="flex flex-col gap-1 py-2" data-testid="costs-month" data-month={m.month}>
            <div className="flex items-center justify-between gap-3">
              <span className="font-semibold capitalize">{monthText(m.month, lang)}</span>
              <span className="font-semibold tabular-nums">{m.costs > 0 ? fmtMoney(m.amount, lang) : '—'}</span>
            </div>
            <p className="meta">
              {(Object.entries(m.byKind) as Array<[string, number]>).map(([k, v]) => `${costKindText(t, k)} ${fmtMoney(v, lang)}`).join(' · ') || t('costs.months.noCost')}
              {' · '}{m.litres !== null ? `${fmtQty(m.litres, lang)} L` : m.fills > 0 ? t('costs.sum.fills', { count: m.fills }) : t('costs.months.noFill')}
              {' · '}{m.hours !== null ? `${fmtQty(m.hours, lang)} h` : t('costs.months.noHours')}
            </p>
          </li>
        ))}
      </ul>
    </div>
  )
}

// ---------------------------------------------------------------------------------------------------- the costs of the period
function CostList({ r, s, manage, onDone }: { r: AssetCostReport; s: Sensitive; manage: boolean; onDone: (k: string) => (ok: boolean | undefined) => Promise<void> }) {
  const { t } = useTranslation()
  const { db } = useClub()
  const waiting = useLiveQuery(() => db.outbox.toArray().then(waitingInvoiceCounts), [db]) ?? new Map<string, number>()
  const rows = costRows(r.costs)
  return (
    <div className="flex flex-col gap-2" data-testid="costs-list">
      <h3 className="text-lg font-bold">{t('costs.list.title', { count: rows.filter((c) => !c.voided_at).length })}</h3>
      {rows.length === 0 && <p className="meta" data-testid="costs-list-none">{t('costs.list.none')}</p>}
      {rows.map((c) => <CostItem key={c.id} c={c} s={s} manage={manage} waiting={waiting.get(c.id) ?? 0} onDone={onDone} />)}
    </div>
  )
}

function CostItem({ c, s, manage, waiting, onDone }: { c: CostLine; s: Sensitive; manage: boolean; waiting: number; onDone: (k: string) => (ok: boolean | undefined) => Promise<void> }) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const nameOf = usePersonNames()
  const voided = !!c.voided_at
  return (
    <div className={`card flex flex-col gap-2 ${voided ? 'opacity-90' : ''}`} data-testid="cost-line" data-id={c.id} data-voided={voided ? '1' : '0'}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-semibold">{costKindText(t, c.kind)} · {dayText(c.incurred_on)}</span>
        <span className={`text-lg font-bold tabular-nums ${voided ? 'line-through' : ''}`} data-testid="cost-amount">{fmtMoney(Number(c.amount), lang, c.currency)}</span>
      </div>
      {voided && <p className="text-sm" data-testid="cost-voided"><Pill tone="gray">{t('costs.list.voided')}</Pill> {c.void_reason}</p>}
      {c.note && <p className="whitespace-pre-wrap break-words text-sm" data-testid="cost-note">{c.note}</p>}
      <p className="meta">
        {nameOf(c.actor_person_id) ? t('costs.list.by', { who: nameOf(c.actor_person_id) }) : ''}
        {c.work_order_id ? ` · ${t('costs.list.order')}` : ''}{c.movement_id ? ` · ${t('costs.list.fillUp')}` : ''}
      </p>
      {c.files.map((f) => <InvoiceFile key={f.link_id} f={f} cost={c} />)}
      {waiting > 0 && <p className="meta" data-testid="cost-invoice-waiting">{t('costs.invoice.waiting', { count: waiting })}</p>}
      {manage && !voided && <AttachInvoice costId={c.id} />}
      {manage && !voided && <VoidCost c={c} s={s} onDone={onDone('costs.void.done')} />}
    </div>
  )
}

/** An invoice's file: "PDF · 2,4 Mo", handed to the phone's own viewer (fetched with no-store, never cached, never previewed). */
function InvoiceFile({ f, cost }: { f: CostFile; cost: CostLine }) {
  const { t, i18n } = useTranslation()
  const { transport, org } = useClub()
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)
  const download = async () => {
    setBusy(true); setFailed(false)
    try {
      const blob = await noStore(await transport.attachmentUrl(attachmentStorageKey(org.org_id, f.site_id, f.sha256, f.mime), f.bucket))
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a'); a.href = url; a.download = invoiceFileName(f, cost); a.rel = 'noopener'
      document.body.appendChild(a); a.click(); a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch { setFailed(true) } finally { setBusy(false) }
  }
  return (
    <div className="flex flex-col gap-1" data-testid="cost-file" data-mime={f.mime} data-stored={f.stored ? '1' : '0'}>
      <p className="flex min-w-0 flex-wrap items-center gap-x-2 text-sm">
        <span className="font-semibold">{fileTypeText(f.mime)}</span><span className="meta">· {fileSizeText(f.bytes, i18n.language)}</span>
        {f.caption && <span className="min-w-0 break-words">· {f.caption}</span>}
        {!f.stored && <Pill tone="amber">{t('costs.invoice.notStored')}</Pill>}
      </p>
      <button type="button" className="btn" disabled={busy || !f.stored} onClick={() => void download()} data-testid="cost-file-download">{busy ? t('costs.invoice.downloading') : t('costs.invoice.download')}</button>
      {failed && <Banner tone="warn"><span data-testid="cost-file-error">{t('costs.invoice.downloadFailed')}</span></Banner>}
    </div>
  )
}
const noStore = async (url: string): Promise<Blob> => {
  const r = await fetch(url, { cache: 'no-store', credentials: 'omit' })
  if (!r.ok) throw new Error(String(r.status))
  return r.blob()
}

/** "Joindre la facture": a PDF or a photo, kept with its register command in one local transaction and sent with the next sync (M15-V's lane). */
function AttachInvoice({ costId }: { costId: string }) {
  const { t } = useTranslation()
  const { db, send, org, site } = useClub()
  const input = useRef<HTMLInputElement>(null)
  const { i18n } = useTranslation()
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<{ tone: 'ok' | 'warn'; text: string } | null>(null)
  // release b review (finding 13): an attachment has no delete and a void leaves its file linked, so the file picked is shown (type, size, name) before
  // anything is queued, and attached only on "Joindre"
  const [chosen, setChosen] = useState<PreparedDocument | null>(null)
  const refused = (e: unknown) => setMsg({ tone: 'warn', text: e instanceof DocumentTooLargeError ? t('documents.err.ATTACHMENT_TOO_LARGE') : e instanceof DocumentTypeError ? t('documents.err.ATTACHMENT_TYPE_NOT_ALLOWED')
    : e instanceof PhotoQuotaError ? t('photos.quota') : e instanceof DocumentRefusedError ? t('costs.invoice.refused') : t('errors.unknown') })
  const pick = async (file: File) => {
    setBusy(true); setMsg(null); setChosen(null)
    try { setChosen(await prepareDocument(file)) } catch (e) { refused(e) } finally { setBusy(false) }
  }
  const attach = async () => {
    if (!chosen) return
    setBusy(true); setMsg(null)
    try {
      // the caption is the file's own name: the link is read under finance.read alone, and the command log keeps it "[redacted]" (sensitive_paths, the review's fix)
      await addDocument(db, send, org.org_id, site.site_id, chosen, { entity_type: 'fleet.asset_cost', entity_id: costId, purpose: 'invoice', caption: chosen.name })
      setChosen(null)
      setMsg({ tone: 'ok', text: t('costs.invoice.added') })
    } catch (e) {
      refused(e)
    } finally { setBusy(false) }
  }
  return (
    <div className="flex flex-col gap-1">
      {!chosen && <button type="button" className="btn" disabled={busy} onClick={() => input.current?.click()} data-testid="cost-invoice-pick">{busy ? t('documents.form.preparing') : t('costs.invoice.attach')}</button>}
      {chosen && (
        <div className="flex flex-col gap-2 rounded-xl border-2 border-slate-200 p-3 dark:border-night-line" data-testid="cost-invoice-chosen">
          <p className="min-w-0 break-words text-sm"><span className="font-semibold">{fileTypeText(chosen.mime)}</span> · {fileSizeText(chosen.bytes, i18n.language)} · {chosen.name}</p>
          <p className="meta">{t('costs.invoice.confirmHelp')}</p>
          <span className="flex flex-wrap gap-2">
            <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void attach()} data-testid="cost-invoice-attach">{t('costs.invoice.attachGo')}</button>
            <button type="button" className="btn" disabled={busy} onClick={() => setChosen(null)} data-testid="cost-invoice-cancel">{t('common.cancel')}</button>
          </span>
        </div>
      )}
      <input ref={input} type="file" accept="application/pdf,image/*" className="sr-only" tabIndex={-1} aria-hidden data-testid="cost-invoice-input"
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void pick(f) }} />
      {msg && <Banner tone={msg.tone}><span data-testid="cost-invoice-msg">{msg.text}</span></Banner>}
    </div>
  )
}

/** "Annuler cette saisie": with a reason, in two taps; the cost stays in the list, struck through, and leaves every sum. */
function VoidCost({ c, s, onDone }: { c: CostLine; s: Sensitive; onDone: (ok: boolean | undefined) => Promise<void> }) {
  const { t } = useTranslation()
  const [reason, setReason] = useState('')
  const [sure, setSure] = useState(false)
  const problem = voidProblem(reason)
  return (
    <details className="group rounded-xl border-2 border-slate-200 px-3 dark:border-night-line" data-testid="cost-void">
      <summary className="flex min-h-14 cursor-pointer list-none items-center font-semibold [&::-webkit-details-marker]:hidden" data-testid="cost-void-toggle">{t('costs.void.title')}</summary>
      <div className="flex flex-col gap-3 pb-3">
        <p className="meta">{t('costs.void.help')}</p>
        <Field label={t('costs.void.reason')} hint={t('costs.void.reasonHint')} htmlFor={`void-${c.id}`} error={problem === 'long' ? t('costs.void.reasonLong') : null}>
          <textarea id={`void-${c.id}`} className="input" rows={2} value={reason} onChange={(e) => { setReason(e.target.value); setSure(false) }} data-testid="cost-void-reason" />
        </Field>
        {!sure ? (
          <button type="button" className="btn self-start" disabled={s.busy || !s.online || !!problem} onClick={() => setSure(true)} data-testid="cost-void-go">{t('costs.void.go')}</button>
        ) : (
          <span className="flex flex-wrap gap-2">
            <button type="button" className="btn btn-danger" disabled={s.busy || !s.online} data-testid="cost-void-confirm"
              onClick={() => { setSure(false); void s.command('asset_cost.void@1', { cost_id: c.id, reason: reason.trim() }, 'costs.void.done').then(onDone) }}>{t('costs.void.confirm')}</button>
            <button type="button" className="btn" onClick={() => setSure(false)}>{t('common.cancel')}</button>
          </span>
        )}
        <DisabledHint show={problem === 'empty'} testId="cost-void-hint">{t('costs.void.reasonNeeded')}</DisabledHint>
      </div>
    </details>
  )
}

// ---------------------------------------------------------------------------------------------------- recording a cost
function CostFormFold({ asset, today, s, onDone }: { asset: Machine; today: string; s: Sensitive; onDone: (ok: boolean | undefined) => Promise<void> }) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const { site } = useClub()
  const orders = useWorkOrders(); const movements = useStockMovements()
  const [f, setF_] = useState<CostForm>(() => blankCostForm(today))
  // release b review (finding 7): what was just recorded, said under the button (the form empties; the code field's line is far above), until the next edit
  const [saved, setSaved] = useState<string | null>(null)
  const setF = (x: CostForm) => { setSaved(null); setF_(x) }
  const id = useRef(newId())
  const problem = costProblem(f, today)
  const orderList = useMemo(() => orderOptions(orders ?? [], asset.id), [orders, asset.id])
  const fills = useMemo(() => fillUpOptions(movements ?? [], asset.id), [movements, asset.id])
  const save = () => {
    if (problem) return
    const payload = buildCostPayload(id.current, asset.id, f)
    void s.command('asset_cost.record@1', payload, 'costs.form.saved').then(async (ok) => {
      if (ok) { id.current = newId(); setF_(blankCostForm(today)); setSaved(t('costs.form.savedLine', { kind: costKindText(t, payload.kind), amount: fmtMoney(payload.amount, lang) })) }
      await onDone(ok)
    })
  }
  return (
    <Fold title={t('costs.form.title')} help={t('costs.form.help')} testId="cost-form">
      <form className="flex flex-col gap-3" noValidate onSubmit={(e) => { e.preventDefault(); save() }}>
        <fieldset className="flex flex-col gap-2" data-testid="cost-kind">
          <legend className="text-sm font-semibold">{t('costs.form.kind')}</legend>
          <div className="grid grid-cols-2 gap-2">
            {COST_KINDS.map((k) => (
              <button key={k} type="button" className={`btn !px-3 text-sm ${f.kind === k ? '!border-brand !bg-fairway-light dark:!border-emerald-500 dark:!bg-night' : ''}`} aria-pressed={f.kind === k}
                onClick={() => setF({ ...f, kind: k })} data-testid={`cost-kind-${k}`}>{costKindText(t, k)}</button>
            ))}
          </div>
        </fieldset>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label={t('costs.form.day')} htmlFor={`cost-day-${asset.id}`}>
            <input id={`cost-day-${asset.id}`} type="date" className="input" value={f.day} max={today} onChange={(e) => setF({ ...f, day: e.target.value })} data-testid="cost-day" />
          </Field>
          <Field label={t('costs.form.amount')} hint={t('costs.form.amountHint')} htmlFor={`cost-amount-${asset.id}`}>
            <input id={`cost-amount-${asset.id}`} className="input text-right tabular-nums" inputMode="decimal" autoComplete="off" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="cost-amount-input" />
          </Field>
        </div>
        <Field label={t('costs.form.note')} hint={t('costs.form.noteHint', { max: COST_NOTE_MAX })} htmlFor={`cost-note-${asset.id}`}>
          <input id={`cost-note-${asset.id}`} className="input" maxLength={COST_NOTE_MAX + 20} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="cost-note-input" />
        </Field>
        {orderList.length > 0 && (
          <Field label={t('costs.form.order')} hint={t('common.optional')} htmlFor={`cost-order-${asset.id}`}>
            <select id={`cost-order-${asset.id}`} className="input" value={f.workOrderId} onChange={(e) => setF({ ...f, workOrderId: e.target.value })} data-testid="cost-order">
              <option value="">{t('costs.form.noOrder')}</option>
              {orderList.map((o) => <option key={o.id} value={o.id}>{`${dayText(localDate(o.opened_at, site.timezone))} · ${t(`workorder.kind.${o.kind}`)}${o.complaint ? ` · ${o.complaint.slice(0, 40)}` : ''}`}</option>)}
            </select>
          </Field>
        )}
        {f.kind === 'fuel' && fills.length > 0 && (
          <Field label={t('costs.form.fillUp')} hint={t('common.optional')} htmlFor={`cost-fill-${asset.id}`}>
            <select id={`cost-fill-${asset.id}`} className="input" value={f.movementId} onChange={(e) => setF({ ...f, movementId: e.target.value })} data-testid="cost-fill">
              <option value="">{t('costs.form.noFillUp')}</option>
              {fills.map((m) => <option key={m.id} value={m.id}>{`${dayText(m.occurred_local_date)} · ${fmtQty(Math.abs(Number(m.qty_base)), lang)} ${m.base_unit_code}`}</option>)}
            </select>
          </Field>
        )}
        <button className="btn btn-primary" disabled={s.busy || !s.online || !!problem} data-testid="cost-save">{s.busy ? t('common.saving') : t('costs.form.save')}</button>
        {saved && <Banner tone="ok"><span data-testid="cost-saved">{saved}</span></Banner>}
        <DisabledHint show={!!problem && !saved} testId="cost-save-hint">{problem ? costProblemText(t, problem) : ''}</DisabledHint>
      </form>
    </Fold>
  )
}

// ---------------------------------------------------------------------------------------------------- the machine's financials
function FinancialFold({ asset, r, s, onDone }: { asset: Machine; r: AssetCostReport; s: Sensitive; onDone: (ok: boolean | undefined) => Promise<void> }) {
  const { t, i18n } = useTranslation()
  const [f, setF_] = useState<FinancialForm>(() => financialFormOf(r.financial, i18n.language))
  const [saved, setSaved] = useState(false)
  const setF = (x: FinancialForm) => { setSaved(false); setF_(x) }
  // the form follows what the server holds after a save (the report is read again)
  useEffect(() => { setF_(financialFormOf(r.financial, i18n.language)) }, [r.financial, i18n.language])
  const built = buildFinancialPayload(asset.id, f, r.financial)
  const problem = 'problem' in built ? built.problem : null
  return (
    <Fold title={t('costs.financial.title')} help={t('costs.financial.help')} testId="cost-financial">
      <form className="flex flex-col gap-3" noValidate onSubmit={(e) => { e.preventDefault(); if ('payload' in built) void s.command('asset_financial.set@1', built.payload, 'costs.financial.saved').then(async (ok) => { if (ok) setSaved(true); await onDone(ok) }) }}>
        <Field label={t('costs.financial.purchase')} hint={t('costs.financial.amountHint')} htmlFor={`fin-purchase-${asset.id}`}>
          <input id={`fin-purchase-${asset.id}`} className="input text-right tabular-nums" inputMode="decimal" autoComplete="off" value={f.purchase} onChange={(e) => setF({ ...f, purchase: e.target.value })} data-testid="fin-purchase" />
        </Field>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label={t('costs.financial.life')} hint={t('costs.financial.lifeHint')} htmlFor={`fin-life-${asset.id}`}>
            <input id={`fin-life-${asset.id}`} className="input text-right tabular-nums" inputMode="numeric" autoComplete="off" value={f.life} onChange={(e) => setF({ ...f, life: e.target.value })} data-testid="fin-life" />
          </Field>
          <Field label={t('costs.financial.residual')} hint={t('costs.financial.amountHint')} htmlFor={`fin-residual-${asset.id}`}>
            <input id={`fin-residual-${asset.id}`} className="input text-right tabular-nums" inputMode="decimal" autoComplete="off" value={f.residual} onChange={(e) => setF({ ...f, residual: e.target.value })} data-testid="fin-residual" />
          </Field>
        </div>
        <p className="meta">{t('costs.financial.acquired')}</p>
        <Link to="/assets/$assetId/edit" params={{ assetId: asset.id }} className="btn self-start" data-testid="fin-edit-machine">{t('costs.financial.acquiredLink')}</Link>
        <button className="btn btn-primary" disabled={s.busy || !s.online || !!problem} data-testid="fin-save">{s.busy ? t('common.saving') : t('costs.financial.save')}</button>
        {saved && <Banner tone="ok"><span data-testid="fin-saved">{t('costs.financial.saved')}</span></Banner>}
        <DisabledHint show={!!problem} testId="fin-save-hint">{problem ? financialProblemText(t, problem) : ''}</DisabledHint>
      </form>
    </Fold>
  )
}
